/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectAzureToken } from "../../store/slices/authSlice";

interface EmailDialogProps {
  open: boolean;
  onClose: () => void;
  to?: string;
  subject?: string;
  body?: string;
}

const EmailDialog = ({
  open,
  onClose,
  to = "",
  subject = "",
  body = "",
}: EmailDialogProps) => {
  const [formData, setFormData] = useState({
    to,
    subject,
    body,
  });
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const token = useSelector(selectAzureToken);

  const handleChange =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSend = async () => {
    if (!validateEmail(formData.to)) {
      setError("Ogiltig e-postadress");
      return;
    }

    if (!formData.subject.trim()) {
      setError("Ämne är obligatoriskt");
      return;
    }

    try {
      setIsSending(true);
      setError(null);

      const response = await fetch(
        "https://graph.microsoft.com/v1.0/me/sendMail",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message: {
              subject: formData.subject,
              body: {
                contentType: "Text",
                content: formData.body,
              },
              toRecipients: [
                {
                  emailAddress: {
                    address: formData.to,
                  },
                },
              ],
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      onClose();
    } catch (err) {
      setError("Det gick inte att skicka e-postmeddelandet");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Skicka e-post</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Till"
            value={formData.to}
            onChange={handleChange("to")}
            fullWidth
            required
            error={!!formData.to && !validateEmail(formData.to)}
            helperText={
              formData.to && !validateEmail(formData.to)
                ? "Ogiltig e-postadress"
                : ""
            }
          />

          <TextField
            label="Ämne"
            value={formData.subject}
            onChange={handleChange("subject")}
            fullWidth
            required
          />

          <TextField
            label="Meddelande"
            value={formData.body}
            onChange={handleChange("body")}
            multiline
            rows={4}
            fullWidth
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSending}>
          Avbryt
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={isSending || !formData.to || !formData.subject}
          startIcon={isSending ? <CircularProgress size={20} /> : null}
        >
          Skicka
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDialog;
